import React, { useEffect, useRef, Suspense } from 'react'
import { useAppSelector, useAppDispatch } from './redux/hooks'
import {
  useLocation,
  Routes,
  Route,
  useParams,
  useNavigate,
} from 'react-router-dom'
import Footer from './components/Footer'
import Hero from './components/Hero'
import GlobalError from './components/GlobalError'
import LoadingIndicator from './components/LoadingIndicator'
import DemoMode from './components/DemoMode'
import { getProspect } from './redux/app.slice'

const Main = React.lazy(async () => await import('./pages/Main'))
const Business = React.lazy(async () => await import('./pages/Business'))
const Agreements = React.lazy(async () => await import('./pages/Agreements'))
const Finish = React.lazy(async () => await import('./pages/Finish'))

const CoreLayout = (): JSX.Element | null => {
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const prospect = useAppSelector((state) => state.app.prospect)
  const error = useAppSelector((state) => state.app.error)
  const { prospectId } = useParams<{ prospectId: string }>()
  const navigate = useNavigate()
  const hasRedirected = useRef(false)

  useEffect(() => {
    async function fetchData(): Promise<void> {
      await dispatch(getProspect(prospectId!))
    }
    void fetchData()
  }, [dispatch, prospectId])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  useEffect(() => {
    if (prospect != null && prospectId && !hasRedirected.current) {
      const {
        tenant: { agreements },
        agreementSubmission,
      }: Prospect = prospect

      if (agreementSubmission?.status === 'REJECTED') {
        hasRedirected.current = true
        navigate(`/${prospectId}/agreements/supplement`)
        return
      }

      const flows: Array<{
        condition: Function
        nextRoute: string
      }> = [
          {
            condition: ({ TESA }: Agreements) => !TESA?.accepted,
            nextRoute: '/',
          },
          {
            condition: ({ TESA }: Agreements) => TESA?.accepted,
            nextRoute: '/agreements/supplement',
          },
          {
            condition: ({ TESA_SUPPLEMENT }: Agreements) =>
              TESA_SUPPLEMENT?.accepted,
            nextRoute:
              (prospect.tesaVersion ?? 0) >= 6
                ? '/finish'
                : '/agreements/payment',
          },
          {
            condition: ({ PRICING_AND_PAYMENT_TERMS }: Agreements) =>
              PRICING_AND_PAYMENT_TERMS?.accepted,
            nextRoute: '/finish',
          },
        ]

      if (!prospectId.includes('demo')) {
        hasRedirected.current = true
        const nextRoute: string[] = flows
          .filter((item) => item.condition(agreements))
          .map((item) => item.nextRoute)

        navigate(`/${prospectId}${nextRoute.pop() as string}`)
      }
    }
  }, [prospect, prospectId, navigate])

  if (error === true) {
    return (
      <div className="flex flex-col justify-between h-screen md:block">
        <GlobalError />
      </div>
    )
  }

  if (prospect == null) {
    return null
  }

  return (
    <div className="flex flex-col justify-between h-screen md:block">
      <div>
        {prospectId?.includes('demo') &&
          window.location.hostname !== 'registration.kingenergy.com' && (
            <DemoMode />
          )}
        <Hero />
        <div className="mx-auto w-full max-w-[1200px] flex-1 md:overflow-x-auto">
          <Suspense fallback={<LoadingIndicator />}>
            <Routes>
              <Route index element={<Main />} />
              <Route path="business" element={<Business />} />
              <Route path="agreements/*" element={<Agreements />} />
              <Route path="finish" element={<Finish />} />
            </Routes>
          </Suspense>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default CoreLayout
